/** IFC5, o formato `.ifcx`.
 *
 *  O IFC5 não é o IFC de sempre com outra roupagem. É JSON, e é outra
 *  maneira de pensar o modelo: em vez de entidades com atributos
 *  fixos, há **nós** identificados por um caminho, e sobre cada nó
 *  empilham-se pedaços de informação vindos de sítios diferentes. Um
 *  pedaço diz que o nó é um `IfcWindow`, outro dá-lhe a malha, outro
 *  acrescenta-lhe uma classificação, e outro, que pode vir num
 *  ficheiro à parte entregue semanas depois, acrescenta-lhe uma
 *  resistência ao fogo. Nada disso reescreve o ficheiro original.
 *
 *  Isso é o que o torna interessante para nós e é também o que obriga
 *  este ficheiro a existir: ler um `.ifcx` é sobretudo **compor**.
 *
 *  Três regras, que são o essencial do formato:
 *
 *  1. **Vários nós com o mesmo `path` são um só.** Juntam-se pela
 *     ordem em que aparecem, e o último a falar ganha para a mesma
 *     chave.
 *  2. **`children`** liga um nome a outro nó, e é o que faz a árvore.
 *  3. **`inherits`** aponta para um protótipo, e é de lá que vem
 *     quase sempre a geometria: a janela não tem malha nenhuma, quem
 *     tem é o tipo de janela que ela herda.
 *
 *  Este ficheiro é puro: não conhece o three.js nem o viewer. Recebe
 *  JSON e devolve uma cena descrita em números. É o que permite
 *  testá-lo sem desenhar nada, que é o oposto do que aconteceu com o
 *  guia de dois minutos.
 *
 *  Estado do formato: `ifcx_alpha`. A própria buildingSMART escreve no
 *  esquema que ele cobre os exemplos actuais e não o IFC5 inteiro. O
 *  que está aqui segue os exemplos oficiais, e o que não se reconhece
 *  é ignorado em silêncio em vez de rebentar, porque um formato alfa
 *  vai ganhar chaves que hoje não existem.
 */

export interface NoBruto {
  path: string
  children?: Record<string, string | null>
  inherits?: Record<string, string | null>
  attributes?: Record<string, unknown>
}

export interface FicheiroIfcx {
  header?: { id?: string; ifcxVersion?: string; author?: string }
  imports?: { uri: string }[]
  schemas?: Record<string, unknown>
  data?: NoBruto[]
}

export interface NoComposto {
  path: string
  children: Map<string, string>
  inherits: Map<string, string>
  atributos: Map<string, unknown>
}

/** Uma matriz 4x4 na convenção do USD: vectores em LINHA, e por isso a
 *  translação vive na última linha. Guarda-se como quatro linhas de
 *  quatro. */
export type Matriz = readonly [
  readonly number[],
  readonly number[],
  readonly number[],
  readonly number[],
]

export const IDENTIDADE: Matriz = [
  [1, 0, 0, 0],
  [0, 1, 0, 0],
  [0, 0, 1, 0],
  [0, 0, 0, 1],
]

/**
 * Aplica `a` e depois `b`, na convenção de vectores em linha.
 *
 * Por outras palavras: `v * a * b`. É a ordem que interessa ao descer
 * a árvore, porque a transformação do filho aplica-se antes da do pai.
 */
export function multiplicar(a: Matriz, b: Matriz): Matriz {
  const r: number[][] = [
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ]
  for (let i = 0; i < 4; i += 1) {
    for (let j = 0; j < 4; j += 1) {
      let s = 0
      for (let k = 0; k < 4; k += 1) s += a[i][k] * b[k][j]
      r[i][j] = s
    }
  }
  return r as unknown as Matriz
}

/**
 * A mesma matriz na forma que o three.js quer: um array de 16 em
 * ordem de coluna.
 *
 * Parece que é preciso transpor e não é, e vale a pena perceber
 * porquê, porque o erro contrário dá modelos deslocados e ninguém dá
 * por ele até ver o edifício ao lado do terreno.
 *
 * O USD usa vectores em linha (`v * M`) e o three.js usa vectores em
 * coluna (`A * v`), portanto `A = transposta(M)`. Mas o three.js
 * guarda `A` por colunas, e a coluna `c` de `A` é a linha `c` de `M`.
 * As duas inversões anulam-se: basta despejar as linhas do USD pela
 * ordem em que estão.
 */
export function paraColunas(m: Matriz): number[] {
  return [...m[0], ...m[1], ...m[2], ...m[3]]
}

function ehMatriz(v: unknown): v is number[][] {
  return (
    Array.isArray(v) &&
    v.length === 4 &&
    v.every((l) => Array.isArray(l) && l.length === 4 && l.every((x) => typeof x === 'number'))
  )
}

/** A transformação declarada num nó, se houver uma legível. */
export function transformacaoDe(atributos: ReadonlyMap<string, unknown>): Matriz | null {
  const x = atributos.get('usd::xformop')
  if (!x || typeof x !== 'object') return null
  const t = (x as { transform?: unknown }).transform
  return ehMatriz(t) ? (t as unknown as Matriz) : null
}

/**
 * Junta os ficheiros num só conjunto de nós.
 *
 * A ordem é a das camadas: o ficheiro base primeiro, os que o
 * acrescentam depois. Dentro de cada ficheiro, a ordem dos nós.
 *
 * Um valor `null` em `children` ou `inherits` apaga a ligação. É o que
 * o esquema diz ao escrever `Record<string | null>`, e é o que permite
 * a uma camada tirar alguma coisa em vez de só acrescentar.
 */
export function compor(
  ficheiros: readonly FicheiroIfcx[],
): Map<string, NoComposto> {
  const nos = new Map<string, NoComposto>()
  for (const f of ficheiros) {
    for (const bruto of f.data ?? []) {
      if (!bruto || typeof bruto.path !== 'string' || bruto.path.length === 0) {
        continue
      }
      let no = nos.get(bruto.path)
      if (!no) {
        no = {
          path: bruto.path,
          children: new Map(),
          inherits: new Map(),
          atributos: new Map(),
        }
        nos.set(bruto.path, no)
      }
      juntarLigacoes(no.children, bruto.children)
      juntarLigacoes(no.inherits, bruto.inherits)
      for (const [k, v] of Object.entries(bruto.attributes ?? {})) {
        if (v === null) no.atributos.delete(k)
        else no.atributos.set(k, v)
      }
    }
  }
  return nos
}

function juntarLigacoes(
  destino: Map<string, string>,
  origem: Record<string, string | null> | undefined,
): void {
  for (const [nome, alvo] of Object.entries(origem ?? {})) {
    if (alvo === null) destino.delete(nome)
    else if (typeof alvo === 'string') destino.set(nome, alvo)
  }
}

export interface Malha {
  /** Pontos em coordenadas do mundo, já transformados: x,y,z seguidos. */
  posicoes: Float32Array
  /** Índices dos triângulos. */
  indices: Uint32Array
  /** Cor difusa 0..1, quando declarada. */
  cor: [number, number, number] | null
  opacidade: number | null
}

/** Uma polilinha: eixos de grelha, linhas de referência, e tudo o que
 *  o autor deixou como curva em vez de sólido. Guardam-se os pontos
 *  seguidos, já no mundo. */
export interface Curva {
  posicoes: Float32Array
}

export interface ElementoIfcx {
  path: string
  /** O nó acima na árvore, ou null na raiz. Serve para subir do
   *  `Body` para o elemento a que ele pertence. */
  pai: string | null
  /** O nome do ramo que lá chega, que é o nome que o autor deu. */
  nome: string
  /** O caminho na árvore, para se saber onde se está. */
  caminho: string
  /** `bsi::ifc::class`, quando declarada. */
  classe: string | null
  /** Tudo o que o nó diz, já com o que herda do protótipo. */
  atributos: Map<string, unknown>
  malhas: Malha[]
  curvas: Curva[]
  visivel: boolean
}

/** Uma fronteira de espaço: que elemento limita que espaço.
 *
 *  Vive num nó só dela, que não está na árvore de ninguém: é uma
 *  relação, não uma coisa. Por isso lê-se de todos os nós compostos e
 *  não ao descer a árvore. */
export interface FronteiraDeEspaco {
  elemento: string
  espaco: string
}

export interface CenaIfcx {
  elementos: ElementoIfcx[]
  fronteiras: FronteiraDeEspaco[]
  /** Quantos nós foram compostos ao todo. */
  nos: number
  /** Chaves de atributo que apareceram e que não sabemos ler. Ficam
   *  registadas em vez de desaparecerem: num formato alfa, o que hoje
   *  não se reconhece é a lista de trabalho de amanhã. */
  desconhecidas: string[]
}

const CONHECIDAS = new Set([
  'usd::usdgeom::mesh',
  'usd::usdgeom::basiscurves',
  'usd::usdgeom::visibility',
  'usd::xformop',
  'bsi::ifc::class',
  'bsi::ifc::material',
  'bsi::ifc::spaceBoundary',
  'bsi::ifc::presentation::diffuseColor',
  'bsi::ifc::presentation::opacity',
  'customdata',
])

/** Reconhece as famílias com prefixo, como `bsi::ifc::prop::Height`. */
function conhecida(chave: string): boolean {
  if (CONHECIDAS.has(chave)) return true
  return (
    chave.startsWith('bsi::ifc::prop::') ||
    chave.startsWith(PREFIXO_QUALIFICADO) ||
    chave.startsWith('bsi::ifc-mat::prop::') ||
    chave.startsWith('nlsfb::')
  )
}

/** Desde o ifcx 4.2.0 (LTplus, 24-09-2026), uma propriedade que venha
 *  de um property set escreve-se com o pset no meio:
 *  `bsi::ifc::v5a::Pset_WallCommon::IsExternal`. A forma antiga e
 *  plana, `bsi::ifc::prop::IsExternal`, ficou reservada aos nomes do
 *  esquema oficial do IFC5.
 *
 *  Porquê: duas propriedades com o mesmo nome em psets diferentes
 *  ficavam com a mesma chave, e uma apagava a outra em silêncio. */
const PREFIXO_QUALIFICADO = 'bsi::ifc::v5a::'

export interface PropriedadeIfcx {
  /** O nome como o autor lhe chamou. */
  nome: string
  /** O property set de onde vem, quando a chave o diz. */
  pset: string | null
  /** 'ifc' (do esquema ou de um pset), 'material' ou 'classificacao'. */
  familia: 'ifc' | 'material' | 'classificacao'
  /** A chave inteira, para quem quiser conferir no ficheiro. */
  chave: string
}

/** Lê o que uma chave de atributo diz sobre a propriedade. Null quando
 *  a chave não é de uma propriedade (geometria, presentação, ...). */
export function lerChaveDePropriedade(chave: string): PropriedadeIfcx | null {
  if (chave.startsWith(PREFIXO_QUALIFICADO)) {
    const resto = chave.slice(PREFIXO_QUALIFICADO.length)
    const corte = resto.lastIndexOf('::')
    // Sem pset no meio é uma chave do esquema escrita neste prefixo.
    if (corte < 0) return { nome: resto, pset: null, familia: 'ifc', chave }
    return { nome: resto.slice(corte + 2), pset: resto.slice(0, corte), familia: 'ifc', chave }
  }
  if (chave.startsWith('bsi::ifc::prop::')) {
    return { nome: chave.slice('bsi::ifc::prop::'.length), pset: null, familia: 'ifc', chave }
  }
  if (chave.startsWith('bsi::ifc-mat::prop::')) {
    return { nome: chave.slice('bsi::ifc-mat::prop::'.length), pset: null, familia: 'material', chave }
  }
  if (chave === 'bsi::ifc::material') return { nome: 'Material', pset: null, familia: 'material', chave }
  if (chave === 'nlsfb::class') return { nome: 'NL-SfB', pset: null, familia: 'classificacao', chave }
  return null
}

export interface PropriedadeLida extends PropriedadeIfcx {
  valor: unknown
}

export interface ColisaoDePropriedade {
  nome: string
  /** As chaves que lhe chamam o mesmo, com os valores que trazem. */
  origens: Array<{ chave: string; pset: string | null; valor: unknown }>
}

/**
 * As propriedades de um nó, e as colisões de nome.
 *
 * Duas chaves diferentes podem dar pelo mesmo nome: a mesma propriedade
 * em dois psets, ou a forma plana e a qualificada ao mesmo tempo. O
 * ficheiro guarda as duas, e mostrá-las como uma escondia que há duas.
 * Aqui mostram-se ambas, com o pset ao lado, e diz-se que colidem.
 */
export function propriedadesDoNo(atributos: ReadonlyMap<string, unknown>): {
  propriedades: PropriedadeLida[]
  colisoes: ColisaoDePropriedade[]
} {
  const propriedades: PropriedadeLida[] = []
  for (const [chave, valor] of atributos) {
    const p = lerChaveDePropriedade(chave)
    if (p) propriedades.push({ ...p, valor })
  }
  propriedades.sort((a, b) => a.nome.localeCompare(b.nome, 'pt') || (a.pset ?? '').localeCompare(b.pset ?? '', 'pt'))
  const porNome = new Map<string, PropriedadeLida[]>()
  for (const p of propriedades) {
    const l = porNome.get(p.nome) ?? []
    l.push(p)
    porNome.set(p.nome, l)
  }
  const colisoes: ColisaoDePropriedade[] = []
  for (const [nome, lista] of porNome) {
    if (lista.length < 2) continue
    colisoes.push({
      nome,
      origens: lista.map((p) => ({ chave: p.chave, pset: p.pset, valor: p.valor })),
    })
  }
  return { propriedades, colisoes }
}

/** Os nós que ninguém tem como filho. Um deles é a raiz do projecto; os
 *  outros costumam ser protótipos e materiais, que existem para serem
 *  herdados e não para serem desenhados. */
export function raizes(nos: ReadonlyMap<string, NoComposto>): string[] {
  const filhos = new Set<string>()
  for (const no of nos.values()) {
    for (const alvo of no.children.values()) filhos.add(alvo)
  }
  return [...nos.keys()].filter((p) => !filhos.has(p))
}

function texto(v: unknown): string | null {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') {
    const c = (v as { code?: unknown }).code
    if (typeof c === 'string') return c
  }
  return null
}

function cor(v: unknown): [number, number, number] | null {
  if (!Array.isArray(v) || v.length < 3) return null
  const [r, g, b] = v
  if (typeof r !== 'number' || typeof g !== 'number' || typeof b !== 'number') {
    return null
  }
  return [r, g, b]
}

/**
 * Percorre a árvore e devolve a cena.
 *
 * O que se resolve pelo caminho:
 *
 * - a transformação acumulada, para os pontos saírem já no mundo;
 * - `inherits`, que é de onde vem a geometria na maior parte dos
 *   exemplos: a janela é um nó quase vazio que herda o tipo, e é o
 *   tipo que tem as malhas;
 * - a visibilidade, que se herda para baixo: um ramo invisível não
 *   volta a ficar visível num filho.
 */
export function lerCena(
  nos: ReadonlyMap<string, NoComposto>,
  raiz?: string,
): CenaIfcx {
  const elementos: ElementoIfcx[] = []
  const desconhecidas = new Set<string>()
  const inicio = raiz ? [raiz] : raizes(nos)

  const visitar = (
    path: string,
    nome: string,
    caminho: string,
    acumulada: Matriz,
    visivel: boolean,
    vistos: Set<string>,
    pai: string | null,
  ): void => {
    const no = nos.get(path)
    if (!no) return
    // Um ciclo de herança ou de filhos poria isto a correr para sempre.
    // Acontece em ficheiros mal formados, e um viewer que pendura é
    // pior do que um que diz "não percebi".
    if (vistos.has(path)) return
    const meusVistos = new Set(vistos)
    meusVistos.add(path)

    const atributos = new Map<string, unknown>()
    const filhos = new Map<string, string>()
    // Primeiro o que se herda, depois o que é próprio: o nó manda
    // sobre o protótipo.
    for (const alvo of no.inherits.values()) {
      const proto = nos.get(alvo)
      if (!proto || meusVistos.has(alvo)) continue
      for (const [k, v] of proto.atributos) atributos.set(k, v)
      for (const [k, v] of proto.children) filhos.set(k, v)
    }
    for (const [k, v] of no.atributos) atributos.set(k, v)
    for (const [k, v] of no.children) filhos.set(k, v)

    for (const k of atributos.keys()) {
      if (!conhecida(k)) desconhecidas.add(k)
    }

    const local = transformacaoDe(atributos)
    const mundo = local ? multiplicar(local, acumulada) : acumulada

    const vis = atributos.get('usd::usdgeom::visibility')
    const escondido =
      vis &&
      typeof vis === 'object' &&
      (vis as { visibility?: unknown }).visibility === 'invisible'
    const visivelAqui = visivel && !escondido

    const malha = construirMalha(atributos, mundo)
    const curva = construirCurva(atributos, mundo)
    elementos.push({
      path,
      pai,
      nome,
      caminho,
      classe: texto(atributos.get('bsi::ifc::class')),
      atributos,
      malhas: malha ? [malha] : [],
      curvas: curva ? [curva] : [],
      visivel: visivelAqui,
    })

    for (const [nomeFilho, alvo] of filhos) {
      visitar(
        alvo,
        nomeFilho,
        `${caminho}/${nomeFilho}`,
        mundo,
        visivelAqui,
        meusVistos,
        path,
      )
    }
  }

  for (const p of inicio) visitar(p, p, '', IDENTIDADE, true, new Set(), null)
  return {
    elementos,
    fronteiras: lerFronteiras(nos),
    nos: nos.size,
    desconhecidas: [...desconhecidas].sort(),
  }
}

function referencia(v: unknown): string | null {
  if (!v || typeof v !== 'object') return null
  const r = (v as { ref?: unknown }).ref
  return typeof r === 'string' ? r : null
}

/** As fronteiras de espaço declaradas no ficheiro.
 *
 *  Lêem-se de TODOS os nós compostos e não ao descer a árvore, porque
 *  o nó que declara a fronteira não é filho de ninguém: é uma relação
 *  entre duas coisas, e não uma coisa. Percorrer só a árvore deixava-as
 *  todas de fora sem dar erro nenhum. */
export function lerFronteiras(
  nos: ReadonlyMap<string, NoComposto>,
): FronteiraDeEspaco[] {
  const fora: FronteiraDeEspaco[] = []
  for (const no of nos.values()) {
    const b = no.atributos.get('bsi::ifc::spaceBoundary')
    if (!b || typeof b !== 'object') continue
    const o = b as Record<string, unknown>
    const elemento = referencia(o.relatedelement ?? o.relatedElement)
    const espaco = referencia(o.relatingspace ?? o.relatingSpace)
    if (elemento && espaco) fora.push({ elemento, espaco })
  }
  return fora
}

function construirCurva(
  atributos: ReadonlyMap<string, unknown>,
  mundo: Matriz,
): Curva | null {
  const c = atributos.get('usd::usdgeom::basiscurves')
  if (!c || typeof c !== 'object') return null
  const pontos = (c as { points?: unknown }).points
  if (!Array.isArray(pontos) || pontos.length < 2) return null
  const posicoes = new Float32Array(pontos.length * 3)
  for (let i = 0; i < pontos.length; i += 1) {
    const p = pontos[i]
    if (!Array.isArray(p) || p.length < 3) continue
    const [x, y, z] = p as number[]
    posicoes[i * 3] = x * mundo[0][0] + y * mundo[1][0] + z * mundo[2][0] + mundo[3][0]
    posicoes[i * 3 + 1] = x * mundo[0][1] + y * mundo[1][1] + z * mundo[2][1] + mundo[3][1]
    posicoes[i * 3 + 2] = x * mundo[0][2] + y * mundo[1][2] + z * mundo[2][2] + mundo[3][2]
  }
  return { posicoes }
}

function construirMalha(
  atributos: ReadonlyMap<string, unknown>,
  mundo: Matriz,
): Malha | null {
  const m = atributos.get('usd::usdgeom::mesh')
  if (!m || typeof m !== 'object') return null
  const pontos = (m as { points?: unknown }).points
  const indices = (m as { faceVertexIndices?: unknown }).faceVertexIndices
  if (!Array.isArray(pontos) || !Array.isArray(indices)) return null
  if (pontos.length === 0 || indices.length === 0) return null

  const posicoes = new Float32Array(pontos.length * 3)
  for (let i = 0; i < pontos.length; i += 1) {
    const p = pontos[i]
    if (!Array.isArray(p) || p.length < 3) continue
    const [x, y, z] = p as number[]
    // Vector em linha vezes matriz, com w = 1.
    posicoes[i * 3] = x * mundo[0][0] + y * mundo[1][0] + z * mundo[2][0] + mundo[3][0]
    posicoes[i * 3 + 1] = x * mundo[0][1] + y * mundo[1][1] + z * mundo[2][1] + mundo[3][1]
    posicoes[i * 3 + 2] = x * mundo[0][2] + y * mundo[1][2] + z * mundo[2][2] + mundo[3][2]
  }

  const limpos: number[] = []
  for (const i of indices) {
    if (typeof i === 'number' && i >= 0 && i < pontos.length) limpos.push(i)
  }
  // Índices a mais para o último triângulo: deita-se fora o resto em
  // vez de desenhar lixo.
  const usaveis = limpos.length - (limpos.length % 3)
  if (usaveis === 0) return null

  const opacidade = atributos.get('bsi::ifc::presentation::opacity')
  return {
    posicoes,
    indices: new Uint32Array(limpos.slice(0, usaveis)),
    cor: cor(atributos.get('bsi::ifc::presentation::diffuseColor')),
    opacidade: typeof opacidade === 'number' ? opacidade : null,
  }
}

/**
 * Sobe do nó picado até ao elemento a que ele pertence.
 *
 * Clicar numa parede acerta no nó `Body`, que é onde está a malha, e
 * não na parede. O `Body` não tem classe nenhuma e não diz nada a
 * ninguém: quem clicou quer a parede. Sobe-se até ao primeiro nó com
 * `bsi::ifc::class`, e se não houver nenhum fica-se pelo que foi
 * picado, que continua a ser melhor do que nada.
 */
export function elementoDoNo(
  cena: CenaIfcx,
  path: string,
): ElementoIfcx | null {
  const porPath = new Map(cena.elementos.map((e) => [e.path, e]))
  const picado = porPath.get(path) ?? null
  if (!picado || picado.classe) return picado

  // Sobe-se à procura de uma classe, mas se não houver nenhuma volta-se
  // ao nó picado. Devolver a raiz seria dizer que o projecto inteiro
  // está seleccionado por causa de um clique numa peça.
  let acima = picado
  const vistos = new Set<string>([picado.path])
  while (acima.pai && !vistos.has(acima.pai)) {
    vistos.add(acima.pai)
    const seguinte = porPath.get(acima.pai)
    if (!seguinte) break
    acima = seguinte
    if (acima.classe) return acima
  }
  return picado
}

/** Isto parece mesmo um ficheiro IFC5? */
export function pareceIfcx(v: unknown): v is FicheiroIfcx {
  if (!v || typeof v !== 'object') return false
  const f = v as FicheiroIfcx
  return Array.isArray(f.data) && typeof f.header?.ifcxVersion === 'string'
}
